import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import { auditLogService } from '../../services/auditLogService';
import { AuditLog, AuditLogFilter, auditActionColors } from '../../types/auditLog';

/* -- Action Badge ------------------------------------------------ */

const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const config = auditActionColors[action] || {
    bg: 'bg-slate-500/10',
    text: 'text-slate-400',
    border: 'border-slate-500/20',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${config.bg} ${config.text} border ${config.border}`}
    >
      {action}
    </span>
  );
};

/* -- Changes Diff ------------------------------------------------ */

const ChangesDiff: React.FC<{
  oldValues: string | null;
  newValues: string | null;
  action: string;
  t: (key: string) => string;
}> = ({ oldValues, newValues, action, t }) => {
  const parseJson = (val: string | null): Record<string, unknown> | null => {
    if (!val) return null;
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  };

  const oldObj = parseJson(oldValues);
  const newObj = parseJson(newValues);

  if (!oldObj && !newObj) {
    return <p className="text-slate-400 text-xs italic">{t('auditLogs.table.noChanges')}</p>;
  }

  const allKeysSet = new Set(
    Object.keys(oldObj || {}).concat(Object.keys(newObj || {}))
  );
  const allKeys = Array.from(allKeysSet);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Old Values */}
      {action !== 'Create' && oldObj && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
            {t('auditLogs.table.oldValues')}
          </p>
          <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 space-y-1 max-h-64 overflow-y-auto">
            {allKeys.map((key) =>
              oldObj[key] !== undefined ? (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="text-slate-400 font-medium shrink-0">{key}:</span>
                  <span className="text-red-500 break-all">{String(oldObj[key])}</span>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

      {/* New Values */}
      {action !== 'Delete' && newObj && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
            {t('auditLogs.table.newValues')}
          </p>
          <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3 space-y-1 max-h-64 overflow-y-auto">
            {allKeys.map((key) =>
              newObj[key] !== undefined ? (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="text-slate-400 font-medium shrink-0">{key}:</span>
                  <span className="text-emerald-400 break-all">{String(newObj[key])}</span>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* -- Entity Type Options ----------------------------------------- */

const ENTITY_TYPES = [
  'User',
  'Tenant',
  'Role',
  'RolePermission',
  'Package',
  'TenantUsage',
  'Permission',
];

/* -- Main Page --------------------------------------------------- */

const AuditLogsPage: React.FC = () => {
  const { t } = useTranslation();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const filter: AuditLogFilter = {
        page,
        pageSize,
        ...(actionFilter && { action: actionFilter }),
        ...(entityTypeFilter && { entityType: entityTypeFilter }),
        ...(searchQuery && { userEmail: searchQuery }),
      };
      const result = await auditLogService.getAll(filter);
      setLogs(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, actionFilter, entityTypeFilter, searchQuery]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionFilter, entityTypeFilter, searchQuery]);

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header
        searchPlaceholder={t('auditLogs.searchPlaceholder')}
        onSearch={setSearchQuery}
      />

      <div className="flex-1 overflow-y-auto p-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            {t('auditLogs.title')}
          </h1>
          <p className="text-slate-500 mt-1">{t('auditLogs.subtitle')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">
              {t('auditLogs.filters.allActions')}
            </p>
            <span className="text-2xl font-black text-slate-900">{totalCount}</span>
          </div>
          {['Create', 'Update', 'Delete'].map((action) => {
            const config = auditActionColors[action];
            const count = actionFilter === action ? totalCount : '—';
            return (
              <div key={action} className="bg-white border border-slate-200 p-5 rounded-xl">
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">
                  {action}
                </p>
                <span className={`text-2xl font-black ${config.text}`}>{count}</span>
              </div>
            );
          })}
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-500 text-sm font-medium flex items-center gap-3">
            <span className="material-symbols-outlined">error</span>
            {error}
            <button
              onClick={fetchLogs}
              className="ml-auto text-red-500 hover:text-red-600 underline text-xs"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table Container */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            {/* Action Filter */}
            <div className="flex items-center gap-2">
              {['', 'Create', 'Update', 'Delete'].map((action) => (
                <button
                  key={action}
                  onClick={() => setActionFilter(action)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    actionFilter === action
                      ? 'bg-slate-100 text-slate-700 border border-slate-200'
                      : 'text-slate-500 hover:text-slate-600'
                  }`}
                >
                  {action || t('auditLogs.filters.allActions')}
                </button>
              ))}
            </div>

            {/* Entity Type Dropdown */}
            <select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-600 text-sm font-bold rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary/50"
            >
              <option value="">{t('auditLogs.filters.allEntities')}</option>
              {ENTITY_TYPES.map((et) => (
                <option key={et} value={et}>
                  {et}
                </option>
              ))}
            </select>
          </div>

          {/* Loading / Empty / Table */}
          {isLoading ? (
            <div className="p-16 flex items-center justify-center">
              <div className="flex items-center gap-3 text-slate-500">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">{t('common.loading')}</span>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-4xl text-slate-400 mb-3">
                history
              </span>
              <p className="text-slate-500 font-bold">{t('auditLogs.empty.title')}</p>
              <p className="text-slate-400 text-sm mt-1">
                {totalCount === 0
                  ? t('auditLogs.empty.description')
                  : t('auditLogs.empty.filtered')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[11px] font-black uppercase tracking-widest">
                    <th className="px-6 py-4 w-8" />
                    <th className="px-6 py-4">{t('auditLogs.table.timestamp')}</th>
                    <th className="px-6 py-4">{t('auditLogs.table.user')}</th>
                    <th className="px-6 py-4">{t('auditLogs.table.action')}</th>
                    <th className="px-6 py-4">{t('auditLogs.table.entityType')}</th>
                    <th className="px-6 py-4">{t('auditLogs.table.entityId')}</th>
                    <th className="px-6 py-4">{t('auditLogs.table.ipAddress')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr
                        className="hover:bg-primary/5 group cursor-pointer"
                        onClick={() =>
                          setExpandedRowId(expandedRowId === log.id ? null : log.id)
                        }
                      >
                        <td className="pl-6 py-4">
                          <span
                            className={`material-symbols-outlined text-sm text-slate-400 transition-transform ${
                              expandedRowId === log.id ? 'rotate-90' : ''
                            }`}
                          >
                            chevron_right
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600 font-medium whitespace-nowrap">
                            {formatTimestamp(log.timestamp)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-500">
                            {log.userEmail || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <ActionBadge action={log.action} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600 font-medium">
                            {log.entityType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-slate-400 font-mono">
                            {log.entityId.length > 12
                              ? `${log.entityId.slice(0, 8)}...`
                              : log.entityId}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-slate-400">
                            {log.ipAddress || '—'}
                          </span>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {expandedRowId === log.id && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-slate-50">
                            <div className="pl-8">
                              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">
                                {t('auditLogs.table.changes')}
                              </p>
                              <ChangesDiff
                                oldValues={log.oldValues}
                                newValues={log.newValues}
                                action={log.action}
                                t={t}
                              />
                              {log.serviceName && (
                                <p className="mt-3 text-xs text-slate-400">
                                  {t('auditLogs.table.service')}: {log.serviceName}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500 font-medium tracking-wide">
                {t('auditLogs.pagination.showing', {
                  from,
                  to,
                  total: totalCount,
                })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">chevron_left</span>
                  {t('auditLogs.pagination.previous')}
                </button>
                <span className="text-xs text-slate-400 font-bold px-2">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t('auditLogs.pagination.next')}
                  <span className="material-symbols-outlined text-lg">chevron_right</span>
                </button>
              </div>
            </div>
          )}

          {/* Footer - single page */}
          {!isLoading && logs.length > 0 && totalPages <= 1 && (
            <div className="px-6 py-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 font-medium tracking-wide">
                {t('auditLogs.pagination.showing', {
                  from: 1,
                  to: logs.length,
                  total: totalCount,
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default AuditLogsPage;
